import { Component } from '@theme/component';
import { StandardEvents } from '@shopify/events';
import { getScrollContainer, getScrollTop, scrollTo as scrollPageTo } from '@theme/scroll-container';
(() => {
  const ELEMENT_NAME = 'mcm-horizon-search-grid';

  if (customElements.get(ELEMENT_NAME)) return;

  class McmSearchProductGrid extends Component {
    updatedCallback() {
      super.updatedCallback();
      // Horizon morphs this element in place after a native facet update.
      // Its server markup contains empty per-type grids: discard old cursors
      // and requests, then fetch each open type using the updated page URL.
      this.generation = (this.generation || 0) + 1;
      for (const controller of this.controllers.values()) controller.abort();
      this.controllers.clear();
      this.querySelectorAll('[data-mcm-maker-group]').forEach(group => {
        group.querySelector('[data-mcm-group-grid]').replaceChildren();
        group.dataset.loaded = 'false'; group.dataset.loading = 'false'; group.dataset.nextUrl = '';
        group._mcmFilling = false; group._mcmFailed = false;
        if (group.open) this.ensureLoaded(group);
      });
      this.dataset.ready = 'true';
      this.syncGridView();
    }

    constructor() {
      super();
      this.controllers = new Map();
      this.onResultsUpdate = (event) => {
        if (!this.controlsRoot?.contains(event.target)) return;
        const url = new URL(window.location.href);
        if (url.href === this.pageUrl) return;
        this.saveState();
        // Use the URL already built by Horizon's native facets form.
        window.location.replace(url.href);
      };
      this.syncGridView = () => {
        const view = this.surface?.getAttribute('product-grid-view') || 'default';
        this.querySelectorAll('.mcm-maker-grid-list').forEach(list => list.setAttribute('product-grid-view', view));
      };
      this.onPageHide = () => this.saveState();
      this.onToggle = this.onToggle.bind(this);
      this.onClick = this.onClick.bind(this);
      this.intent = 0;
      this.onIntent = (event) => {
        if (event.type === 'wheel' && event.deltaY <= 0) return;
        if (event.type === 'keydown' && !['ArrowDown', 'PageDown', 'End', ' '].includes(event.key)) return;
        if (event.type === 'keydown' && event.target?.closest?.('input, textarea, select, [contenteditable]')) return;
        if (event.type === 'pointerdown' && event.clientX < document.documentElement.clientWidth) return;
        if (event.type === 'pointermove' && event.buttons !== 1) return;
        this.intent++;
        this.onScroll();
      };
      this.syncStickyOffset = () => {
        const header = document.querySelector('#header-component');
        const bar = this.controlsRoot?.querySelector('.mcm-maker-sticky-filters');
        if (!header || !bar) return;
        const container = getScrollContainer();
        const isDocument = container === document.scrollingElement || container === document.documentElement;
        const containerTop = isDocument ? 0 : container.getBoundingClientRect().top + container.clientTop;
        const offset = Math.max(0, header.getBoundingClientRect().bottom - containerTop);
        bar.style.setProperty('--mcm-maker-sticky-top', `${offset}px`);
      };
      this.onScroll = () => {
        if (this.frame) return;
        this.frame = requestAnimationFrame(() => { this.frame = null; this.syncStickyOffset(); this.checkScroll(); });
      };
    }

    connectedCallback() {
      super.connectedCallback();
      if (this._connected) return;
      this._connected = true;
      this.dataset.ready = 'true';
      if (this.querySelector('[data-mcm-maker-group]')) this.querySelector('[data-mcm-all-products]')?.removeAttribute('open');

      this.sectionId = this.dataset.id;
      this.pageUrl = window.location.href;
      this.controlsRoot = this.closest('results-list');
      this.surface = this.parentElement;
      this.headerObserver = new ResizeObserver(this.syncStickyOffset);
      const header = document.querySelector('#header-component');
      if (header) this.headerObserver.observe(header);
      this.syncStickyOffset();
      this.controlsRoot?.addEventListener(StandardEvents.collectionUpdate, this.onResultsUpdate);
      this.stateKey = 'mcm-search-sections:' + window.location.pathname + ':' + new URL(window.location.href).searchParams.toString() + ':' + this.sectionId;
      this.restoreState();
      window.addEventListener('pagehide', this.onPageHide);
      this.viewObserver = new MutationObserver(this.syncGridView);
      this.viewObserver.observe(this.surface, { attributes: true, attributeFilter: ['product-grid-view'] });
      this.syncGridView();
      this.querySelectorAll('[data-mcm-close-group]').forEach(button => { button.textContent = 'Close Section'; });
      for (const type of ['wheel', 'touchmove', 'keydown', 'pointerdown', 'pointermove']) document.addEventListener(type, this.onIntent, { passive: true, capture: true });
      document.addEventListener('scroll', this.onScroll, { passive: true, capture: true });
      window.addEventListener('resize', this.onScroll);
      this.addEventListener('toggle', this.onToggle, true);
      this.addEventListener('click', this.onClick);

      this.querySelectorAll('[data-mcm-maker-group]').forEach((group) => this.syncToggleIcon(group));

      const loads = [...this.querySelectorAll('[data-mcm-maker-group][open]')].map(group => this.ensureLoaded(group));
      Promise.allSettled(loads).then(() => this.restoreScroll());
    }

    disconnectedCallback() {
      this._connected = false;
      super.disconnectedCallback();
      this.saveState();
      window.removeEventListener('pagehide', this.onPageHide);
      (this.scrollTimers || []).forEach(clearTimeout);
      this.viewObserver?.disconnect();
      this.headerObserver?.disconnect();
      this.controlsRoot?.removeEventListener(StandardEvents.collectionUpdate, this.onResultsUpdate);
      this.generation = (this.generation || 0) + 1;
      for (const type of ['wheel', 'touchmove', 'keydown', 'pointerdown', 'pointermove']) document.removeEventListener(type, this.onIntent, true);
      document.removeEventListener('scroll', this.onScroll, true);
      window.removeEventListener('resize', this.onScroll);
      if (this.frame) cancelAnimationFrame(this.frame);
      this.removeEventListener('toggle', this.onToggle, true);
      this.removeEventListener('click', this.onClick);

      for (const [group, controller] of this.controllers) {
        controller.abort();
        group.dataset.loading = 'false';
        group.removeAttribute('aria-busy');
      }
      this.controllers.clear();
    }

    groupKey(group) {
      return this.getRawTypes(group).sort().join('|');
    }

    restoreState() {
      let state = null;
      try { state = JSON.parse(sessionStorage.getItem(this.stateKey)); } catch (_) { /* Use count defaults. */ }
      this.returnPosition = null;
      this.returnIntent = this.intent;
      if (window.performance?.getEntriesByType('navigation')[0]?.type === 'back_forward') {
        try { this.returnPosition = JSON.parse(sessionStorage.getItem('mcm-search-scroll:' + this.pageUrl)); } catch (_) {}
      }
      const limit = window.matchMedia('(max-width: 749px)').matches ? 30 : 50;
      this.querySelectorAll('[data-mcm-maker-group]').forEach(group => {
        const key = this.groupKey(group);
        const saved = state?.groups?.[key];
        const count = Number(group.dataset.mcmProductCount);
        group.open = typeof saved === 'boolean' ? saved : Number.isFinite(count) && count <= limit;
        const visible = Number(this.returnPosition?.visible?.[key]);
        if (group.open && Number.isFinite(visible) && visible >= 18) group._mcmRestoreVisible = visible;
      });
      if (state?.view) {
        this.surface.setAttribute('product-grid-view', state.view);
        this.controlsRoot?.querySelectorAll('input[name="grid"],input[name="grid-mobile"]').forEach(input => { input.checked = input.value === state.view; });
      }
    }

    restoreScroll() {
      const saved = this.returnPosition;
      if (!saved || !Number.isFinite(saved.y)) return;
      const intent = this.returnIntent;
      if (this.intent !== intent) { this.returnPosition = null; return; }
      const restore = () => {
        if (!this.isConnected || this.intent !== intent) return;
        scrollPageTo({ top: saved.y, left: 0, behavior: 'instant' });
      };
      // Rebuilt cards and image layout need a frame before the page has its old height.
      requestAnimationFrame(() => requestAnimationFrame(restore));
      this.scrollTimers = [150, 500, 1200].map(delay => setTimeout(restore, delay));
      this.scrollTimers.push(setTimeout(() => { this.returnPosition = null; }, 1300));
    }

    saveState() {
      if (!this.stateKey) return;
      try {
        let state = JSON.parse(sessionStorage.getItem(this.stateKey)) || {};
        state.groups = state.groups || {};
        this.querySelectorAll('[data-mcm-maker-group]').forEach(group => { state.groups[this.groupKey(group)] = group.open; });
        state.view = this.surface?.getAttribute('product-grid-view') || 'default';
        sessionStorage.setItem(this.stateKey, JSON.stringify(state));
        if (this.pageUrl && !this.returnPosition) {
          const visible = {};
          this.querySelectorAll('[data-mcm-maker-group]').forEach(group => { visible[this.groupKey(group)] = group._mcmVisible || 18; });
          sessionStorage.setItem('mcm-search-scroll:' + this.pageUrl, JSON.stringify({ y: getScrollTop(), visible }));
        }
      } catch (_) { /* Section controls remain usable if storage is unavailable. */ }
    }

    onToggle(event) {
      const group = event.target.closest?.('[data-mcm-maker-group]');
      if (!group || !this.contains(group)) return;

      this.syncToggleIcon(group);
      this.saveState();
      if (group.open) this.ensureLoaded(group);
    }

    syncToggleIcon(group) {
      if (!group) return;
      const icon = group.querySelector(':scope > summary .mcm-toggle-icon');
      if (icon) icon.textContent = group.open ? '−' : '+';
    }

    onClick(event) {
      const closeButton = event.target.closest?.('[data-mcm-close-group]');
      if (closeButton && this.contains(closeButton)) {
        const group = closeButton.closest('[data-mcm-maker-group]');
        if (!group) return;

        const summary = group.querySelector(':scope > summary');
        group.open = false;
        this.syncToggleIcon(group);
        this.saveState();

        requestAnimationFrame(() => {
          group.scrollIntoView({
            behavior: window.matchMedia('(prefers-reduced-motion: reduce)').matches ? 'auto' : 'smooth',
            block: 'start',
          });
          if (summary) summary.focus({ preventScroll: true });
        });
        return;
      }

      const button = event.target.closest?.('[data-mcm-load-more]');
      if (!button || !this.contains(button)) return;

      const group = button.closest('[data-mcm-maker-group]');
      if (!group) return;

      if (button.dataset.retry === 'true') {
        this.fillGroup(group, true);
        return;
      }

      if (!group.dataset.nextUrl) return;
      this.fillGroup(group);
    }

    ensureLoaded(group) {
      if (group.dataset.loaded === 'true' || group.dataset.loading === 'true') return;
      group._mcmVisible = group._mcmRestoreVisible || 18;
      delete group._mcmRestoreVisible;
      return this.fillGroup(group, true);
    }

    showCards(group) {
      const cards = group.querySelector('[data-mcm-group-grid]').children;
      [...cards].forEach((card, index) => { card.hidden = index >= group._mcmVisible; });
    }

    checkScroll() {
      const groups = [...this.querySelectorAll('[data-mcm-maker-group]')].filter((group) => {
        if (!group.open || group.dataset.loaded !== 'true' || group._mcmFilling || group._mcmFailed || group._mcmIntent === this.intent) return false;
        const grid = group.querySelector('[data-mcm-group-grid]');
        return (group.dataset.nextUrl || grid.children.length > group._mcmVisible) && grid.getBoundingClientRect().bottom <= window.innerHeight + 400;
      });
      if (groups.length) this.fillGroup(groups[0]);
    }

    async fillGroup(group, retry = false) {
      if (group._mcmFilling) return;
      const generation = this.generation;
      group._mcmFilling = true;
      group._mcmIntent = this.intent;
      if (!retry) group._mcmVisible += 18;
      group._mcmFailed = false;
      const grid = group.querySelector('[data-mcm-group-grid]');
      const visited = new Set();
      try {
        this.showCards(group);
        while (generation === this.generation && this.isConnected && group.open && grid.children.length < group._mcmVisible && (group.dataset.loaded !== 'true' || group.dataset.nextUrl)) {
          const next = group.dataset.loaded === 'true' ? group.dataset.nextUrl : null;
          const key = next || 'first';
          if (visited.has(key)) throw new Error('Repeated product pagination URL');
          visited.add(key);
          await this.loadPage(group, next);
          if (generation !== this.generation) return;
          this.showCards(group);
          if (group._mcmFailed) break;
        }
      } catch (error) {
        this.showError(group);
      } finally {
        if (generation === this.generation) group._mcmFilling = false;
        if (generation === this.generation && this.isConnected && group._mcmIntent !== this.intent) this.onScroll();
      }
    }

    showError(group) {
      group._mcmFailed = true;
      const status = group.querySelector('[data-mcm-status]');
      const text = group.querySelector('[data-mcm-status-text]');
      const button = group.querySelector('[data-mcm-load-more]');
      if (status) status.hidden = false;
      if (text) text.textContent = 'Products could not be loaded. Please try again.';
      if (button) { button.hidden = false; button.disabled = false; button.dataset.retry = 'true'; button.textContent = 'Try again'; }
    }

    getRawTypes(group) {
      return [...group.querySelectorAll('[data-mcm-raw-type]')]
        .map((node) => node.dataset.mcmRawType)
        .filter(Boolean);
    }

    buildRequestUrl(group, nextHref = null) {
      const url = new URL(window.location.href);
      url.searchParams.delete('page');
      url.searchParams.delete('section_id');
      url.searchParams.delete('filter.p.product_type');
      if (!url.searchParams.get('sort_by')) url.searchParams.set('sort_by', 'relevance');

      if (nextHref) {
        const next = new URL(nextHref, window.location.origin);
        const nextPage = next.searchParams.get('page');
        if (nextPage) url.searchParams.set('page', nextPage);
      }

      for (const rawType of this.getRawTypes(group)) {
        url.searchParams.append('filter.p.product_type', rawType);
      }

      url.searchParams.set('section_id', this.sectionId);
      return url;
    }

    async loadPage(group, nextHref) {
      if (group.dataset.loading === 'true') return;

      const previousController = this.controllers.get(group);
      if (previousController) previousController.abort();

      const controller = new AbortController();
      this.controllers.set(group, controller);
      group.dataset.loading = 'true';
      group.setAttribute('aria-busy', 'true');

      const status = group.querySelector('[data-mcm-status]');
      const statusText = group.querySelector('[data-mcm-status-text]');
      const button = group.querySelector('[data-mcm-load-more]');

      if (status) status.hidden = false;
      if (statusText) statusText.textContent = nextHref ? 'Loading more products…' : 'Loading products…';
      if (button) {
        button.disabled = true;
        button.dataset.retry = 'false';
        button.textContent = button.dataset.defaultLabel || 'Load more';
      }

      try {
        const requestUrl = this.buildRequestUrl(group, nextHref);
        const response = await fetch(requestUrl.toString(), {
          signal: controller.signal,
          headers: { 'X-Requested-With': 'XMLHttpRequest' },
        });

        if (!response.ok) throw new Error(`Maker group request failed: ${response.status}`);

        const html = await response.text();
        if (!this.isConnected || controller.signal.aborted) return;
        const documentFragment = new DOMParser().parseFromString(html, 'text/html');
        const payload = documentFragment.querySelector('[data-mcm-maker-results]');

        if (!payload) throw new Error('Maker group response did not contain the expected results payload.');

        const targetGrid = group.querySelector('[data-mcm-group-grid]');
        if (!targetGrid) throw new Error('Maker group is missing its product grid.');

        const existingIds = new Set(
          [...targetGrid.querySelectorAll('[data-mcm-product-id]')]
            .map((item) => item.dataset.mcmProductId)
            .filter(Boolean)
        );

        const incomingCards = [...payload.querySelectorAll('[data-mcm-product-id]')];
        let appended = 0;

        for (const card of incomingCards) {
          const productId = card.dataset.mcmProductId;
          if (productId && existingIds.has(productId)) continue;
          if (productId) existingIds.add(productId);
          card.hidden = targetGrid.children.length >= group._mcmVisible;
          targetGrid.appendChild(card);
          appended += 1;
        }

        group.dataset.loaded = 'true';
        group.dataset.nextUrl = payload.dataset.nextUrl || '';
        group._mcmFailedNextHref = null;

        if (button) {
          button.dataset.retry = 'false';
          button.textContent = button.dataset.defaultLabel || 'Load more';
          button.hidden = true;
          button.disabled = false;
        }

        if (status) status.hidden = true;

        if (incomingCards.length === 0 && targetGrid.children.length === 0) {
          if (status) status.hidden = false;
          if (statusText) statusText.textContent = 'No products match the current filters in this section.';
        } else if (appended === 0 && nextHref && group.dataset.nextUrl) {
          // Defensive escape hatch: do not auto-chain pages. A shopper can click again.
          if (status) status.hidden = true;
        }
      } catch (error) {
        if (error.name === 'AbortError') return;
        console.error('[MCM Maker Grid]', error);

        this.showError(group);
        group._mcmFailedNextHref = nextHref;
        if (status) status.hidden = false;
        if (statusText) statusText.textContent = 'Products could not be loaded. Please try again.';
        if (button) {
          button.hidden = false;
          button.disabled = false;
          button.dataset.retry = 'true';
          button.textContent = 'Try again';
        }
      } finally {
        if (this.controllers.get(group) === controller) {
          this.controllers.delete(group);
          group.dataset.loading = 'false';
          group.removeAttribute('aria-busy');
        }
      }
    }
  }

  customElements.define(ELEMENT_NAME, McmSearchProductGrid);
})();
